import {Spec} from 'immutability-helper';
import $ from 'jquery';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import {TagSchema} from 'constants/RegistryConstants';
import * as context from 'Context';
import {SchemaTag} from 'folder-items/FileConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import AppDispatcher from 'lib/Dispatcher';
import {EntryProtocol, EntryVersion, ExternalProtocol, FromServerProtocol} from 'notebook/ELNConstants';
import {
  ToServerTemplate,
  Template,
  TemplateCollection,
  TemplateCollectionSummary,
  TemplateUpdateObject,
  TemplateUpdateResponse,
} from 'notebook/entry-templates/TemplateConstants';
import TemplateDAO, {TemplatesByVersion} from 'notebook/entry-templates/TemplateDAO';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import TemplateUtil from 'notebook/entry-templates/TemplateUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {EditorStateMetadata} from 'notebook/store/VersionedItemSubstore';
import {ACTION_RESET_VERSION} from 'notebook/store/VersioningConstants';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {MultidocTableOpMap} from 'notebook/tables/excess/ops/TableOpConstants';
// @ts-expect-error
import EntryVersionUtil from 'notebook/util/EntryVersionUtil';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import pick from 'util/pick';

import {TemplateSummaryGraphQL} from '../../../graphql-fragments/notebook/templates/__generated/TemplateSummaryGraphQL';

interface Param {
  showNote?: boolean;
  showTimestamps?: boolean;
  indexSlice?: {start: number; end: number};
  deferred?: JQueryDeferred<void>;
  success?: () => void;
  versionId?: string | number | null;
}

interface SaveFileDataParam {
  data: TemplateUpdateObject;
  success?: () => void;
}

const templateToTemplateSummaryGraphQL = (template: Template): TemplateSummaryGraphQL => ({
  __typename: 'TemplateNode',
  ...template,
  templateCollection: {
    __typename: 'TemplateCollectionNode',
    ...template.templateCollection,
  },
  editUrl: template.editURL,
  archiveRecord: null,
  creator: {__typename: 'UserNode', id: template.creatorId, name: template.creator},
});

const TemplateActions = {
  print: (templateId: string, param: Param = {}): void => {
    const showNote = param.showNote ?? false;
    const showTimestamps = param.showTimestamps ?? false;
    const {indexSlice} = param;
    const state = TemplateStore.getEditorState(templateId);

    const versionId = state.openVersionId;
    let args = `versionId=${versionId}`;
    if (showNote) {
      args += '&note=true';
    }
    if (indexSlice != null) {
      args += `&startIndex=${indexSlice.start}&endIndex=${indexSlice.end}`;
    }
    if (showTimestamps) {
      args += `&showTimestamps=${showTimestamps}`;
    }

    window.open(`${window.location.pathname}/view?${args}#print`, '_blank');
  },

  fetchCollections(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_COLLECTIONS_FETCH,
    });
    TemplateDAO.fetchCollections('read')
      .done((collections: Array<TemplateCollectionSummary>) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_COLLECTIONS_FETCH_COMPLETED,
          collections,
        });
      })
      .fail((err) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_COLLECTIONS_FETCH_FAILED,
        });
        notifications.notifyError(err);
      });
  },

  activateCollection(id: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_COLLECTION_FETCH,
      collectionId: id,
    });
    TemplateDAO.fetchCollection(id)
      .done((collection: TemplateCollection) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_COLLECTION_FETCH_COMPLETED,
          collection,
        });
      })
      .fail((err) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_COLLECTION_FETCH_FAILED,
        });
        notifications.notifyError(err);
      });
  },

  updateProtocol(templateId: string, template: Template, protocolCid: string, op: Spec<EntryProtocol>): void {
    TemplateActions.update(
      templateId,
      TemplateUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion) {
          return EntryVersionUtil.withEntryProtocolUpdated(entryVersion, protocolCid, op);
        },
      })
    );
  },

  addProtocol(templateId: string, template: Template, protocol: ExternalProtocol | FromServerProtocol): void {
    const cidIdMap = {}; // pass to EntryVersionUtil.withProtocolAdded which will populate the map.
    const templateUpdate = TemplateUtil.entryVersionUpdate({
      $apply: (entryVersion) => EntryVersionUtil.withProtocolAdded(entryVersion, protocol, cidIdMap),
    });
    TemplateActions.update(templateId, templateUpdate, {cidIdMap});
  },

  removeProtocol(templateId: string, template: Template, epCid: string): void {
    TemplateActions.update(
      templateId,
      TemplateUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion) {
          return EntryVersionUtil.withEntryProtocolRemoved(entryVersion, epCid);
        },
      })
    );
  },

  revertProtocol(
    templateId: string,
    template: Template,
    revertedEntryProtocol: EntryProtocol,
    restoreData: SelectionRestoreData
  ): void {
    TemplateActions.revert(
      templateId,
      TemplateUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion) {
          return EntryVersionUtil.withEntryProtocolReverted(entryVersion, revertedEntryProtocol);
        },
      }),
      restoreData
    );
  },

  reorderProtocols(templateId: string, template: Template, cids: Array<string>): void {
    TemplateActions.update(
      templateId,
      TemplateUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion) {
          return EntryVersionUtil.withEntryProtocolsReordered(entryVersion, cids);
        },
      })
    );
  },

  registerObjects(templateId: string, cidIdMap: CidIdMap): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_REGISTER_OBJECTS,
      templateId,
      cidIdMap,
    });
  },

  updateNote(
    templateId: string,
    note: EntryNote,
    structuredTableMappings: Array<StructuredTableMapping> | undefined = undefined,
    param: {
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
      multidocTableOp?: MultidocTableOpMap;
    } = {}
  ): void {
    TemplateActions.update(
      templateId,
      TemplateUtil.entryVersionUpdate({
        $merge: {
          note,
          ...(structuredTableMappings && {
            structuredTableMappings,
          }),
        },
      }),
      param
    );
  },

  setName(templateId: string, name: string): void {
    const templateSummary = TemplateStore.getEditorState(templateId).template;
    TemplateActions.update(templateId, {$merge: {name}});
    writeFragmentsToCache(getFragmentWrapper('TemplateSummary'), [
      templateToTemplateSummaryGraphQL({...templateSummary, name}),
    ]);
  },

  /**
   * Applies `op` to the template with id `templateId`, and merges in the specified `cidIdMap`.
   * If `op` introduces new models, it should specify the ids in `cidIdMap`.
   */
  update(
    templateId: string,
    op: Spec<Template>,
    param: {
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
      cidIdMap?: CidIdMap;
      bumpVersion?: boolean;
      multidocTableOp?: MultidocTableOpMap;
      performTableEvaluation?: boolean;
    } = {}
  ): void {
    const {cidIdMap, bumpVersion} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_UPDATE,
      templateId,
      op,
      cidIdMap: cidIdMap != null ? cidIdMap : {},
      bumpVersion: bumpVersion != null ? bumpVersion : true,
      updatedItemPartIndexes: param.updatedItemPartIndexes,
      canIncrementalSave: param.canIncrementalSave,
      multidocTableOp: param.multidocTableOp,
      performTableEvaluation: param.performTableEvaluation ?? true,
    });
  },

  revert(templateId: string, op: Spec<Template>, restoreData: SelectionRestoreData): void {
    // Replace the open template with the specified data.
    // Useful to reset the state to an older state.
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_REVERT,
      templateId,
      op,
      restoreData,
      multidocTableOp: {[templateId]: 'eval-document'},
    });
  },

  // TODO: Don't pass in deferred. Note: Only ProxyFile should be passing in deferred.
  loadVersion(templateId: string, versionId: string | number, param?: Param): void {
    const deferred = param?.deferred;
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_LOAD_VERSION,
      templateId,
      versionId,
    });
    TemplateDAO.getVersion(templateId, String(versionId))
      .done((response: Template) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_LOAD_VERSION_COMPLETED,
          templateId,
          versionId,
          version: response,
        });
        if (deferred != null) {
          deferred.resolve();
        }
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_LOAD_VERSION_FAILED,
          templateId,
          versionId,
        });
        if (deferred != null) {
          deferred.reject(response);
        }
        notifications.notifyError(response);
      });
  },

  fetchVersions(templateId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_FETCH_VERSIONS,
      templateId,
    });
    TemplateDAO.getVersions(templateId)
      .done((response: TemplatesByVersion) => {
        AppDispatcher.dispatch(
          // eslint-disable-next-line no-restricted-syntax
          _.extend(
            {
              actionType: EventConstants.TEMPLATE_FETCH_VERSIONS_COMPLETED,
              templateId,
              versions: response.data,
            },
            pick(response, 'parent')
          )
        ); // parent key present with null value: the parent was deleted
        // omitted parent key: there is no parent.
      })
      .fail(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_FETCH_VERSIONS_FAILED,
          templateId,
        });
      });
  },

  delete(templateId: string, param: {success?: () => void}): void {
    const {success} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_DELETE,
      templateId,
    });
    TemplateDAO.delete(templateId)
      .done(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_DELETE_COMPLETED,
          templateId,
        });
        if (typeof success === 'function') {
          success();
        }
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_DELETE_FAILED,
          templateId,
          error: response,
        });
        notifications.notifyError(response);
      });
  },

  /**
   * Update non-versioned fields (name, template collection) via an immediate request that is
   * processed non-optimistically.
   *
   * @param  {String} templateId
   * @param  {Object} An object containing:
   *                    data: {name, templateCollectionId}
   *                    success: an optional callback fired on success
   */
  async saveFileData(templateId: string, {data, success}: SaveFileDataParam): Promise<void> {
    try {
      const templateUpdateResponse = await TemplateDAO.update(templateId, data);
      if (templateUpdateResponse == null) {
        throw new Error('Template not found');
      }
      const {editURL, templateCollection} = templateUpdateResponse;
      const {template} = TemplateStore.getEditorState(templateId);
      const mergeData: Partial<Template> = {templateCollection, editURL};
      if (data.name != null) {
        mergeData.name = data.name;
      }
      writeFragmentsToCache(getFragmentWrapper('TemplateSummary'), [
        templateToTemplateSummaryGraphQL({...template, templateCollection, editURL}),
      ]);
      TemplateActions.update(
        templateId,
        {$merge: mergeData},
        {bumpVersion: false, performTableEvaluation: false}
      );
      if (typeof success === 'function') {
        success();
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.notifyError(error);
    }
  },

  resetToOpenVersion(templateId: string): void {
    const {isLatest} = TemplateStore.getEditorState(templateId);
    assert(!isLatest, 'Unexpected reset version while on latest version');
    context.using({immediateAutoSaverQueueSaveAction: ACTION_RESET_VERSION}, this, () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.TEMPLATE_RESET_TO_OPEN_VERSION,
        templateId,
      });
    });
  },

  /**
   * Save the open template version via a throttled request.
   *
   * @param  {String} templateId
   */
  save(templateId: string): JQueryPromise<boolean> {
    // Saves the open template to the server.
    const getData = (): {
      openVersion: number;
      serverTemplate: ToServerTemplate & {metadata: EditorStateMetadata};
    } => {
      const {template, openVersion, metadata} = TemplateStore.getEditorState(templateId);
      const serverTemplate = {...TemplateStore.prepareForSave(templateId, template), metadata};
      return {openVersion, serverTemplate};
    };

    // This is saved here in case the template is removed from store before save finishes.
    // TODO: remove after adding refcounts
    const dataAtSaveTime = getData();

    // Create a deferred promise that will be resolved once the save has completed.
    const deferredPromise = $.Deferred<boolean>();
    benchlingUtil.asyncThrottle(`template-save-${templateId}`, () => {
      let dataToUse;
      if (TemplateStore.inStore(templateId)) {
        if (!TemplateStore.hasUnsavedChanges(templateId)) {
          return deferredPromise.resolve(false).promise();
        }
        dataToUse = getData();
      } else {
        dataToUse = dataAtSaveTime;
      }

      const {openVersion, serverTemplate} = dataToUse;
      AppDispatcher.dispatch({
        actionType: EventConstants.TEMPLATE_SAVE,
        templateId,
      });
      const xhr = TemplateDAO.update(templateId, serverTemplate)
        .done((response: TemplateUpdateResponse) => {
          const {cidIdMap, cidNotePartProvenanceIdMap, editURL, templateCollection} = response;
          AppDispatcher.dispatch({
            actionType: EventConstants.TEMPLATE_SAVE_COMPLETED,
            templateId,
            savedVersion: openVersion,
            versionId: cidIdMap[serverTemplate.templateVersion.cid],
            cidIdMap,
            cidNotePartProvenanceIdMap,
            serverData: {
              editURL,
              templateCollection,
            },
          });
          if (TemplateStore.inStore(templateId)) {
            const {template} = TemplateStore.getEditorState(templateId);
            writeFragmentsToCache(getFragmentWrapper('TemplateSummary'), [
              templateToTemplateSummaryGraphQL({...template, editURL, templateCollection}),
            ]);
            TemplateActions.fetchVersions(templateId);
          }
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        })
        .fail((response) => {
          const friendlyMessage = notifications.getFriendlyError(response);
          if (friendlyMessage != null) {
            notifications.error(friendlyMessage, {id: 'template-save-fail', duration: 0});
          }
          AppDispatcher.dispatch({
            actionType: EventConstants.TEMPLATE_SAVE_FAILED,
            templateId,
            error: response,
          });
          TemplateStore.markAllForUpdate(templateId);
          // Resolve the deferred promise to signal that the save has failed.
          deferredPromise.resolve(false);
        });
      return xhr;
    });
    // Return the deferred promise so that the caller can wait for the save to complete.
    return deferredPromise.promise();
  },

  // TODO: Don't pass in deferred. Note: Only ProxyFile should be passing in deferred.
  open(templateId: string, param: Param): void {
    let xhrTemplate;
    const {versionId, deferred} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_OPEN,
      templateId,
    });
    if (versionId != null) {
      xhrTemplate = TemplateDAO.getVersion(templateId, String(versionId));
    } else {
      xhrTemplate = TemplateDAO.get(templateId);
    }

    const xhrTemplateCollection = xhrTemplate.then((response: Template) => {
      const {templateCollection} = response;
      return TemplateDAO.fetchCollection(templateCollection.id);
    });

    $.when<Template | TemplateCollection>(xhrTemplate, xhrTemplateCollection)
      // @ts-expect-error type is incorrect for $.when
      .done(([template], [templateCollection]) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_OPEN_COMPLETED,
          templateId,
          template,
          templateCollection,
        });
        TemplateActions.fetchVersions(templateId);
        if (deferred != null) {
          deferred.resolve();
        }
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.TEMPLATE_OPEN_FAILED,
          templateId,
          error: response,
        });
        if (deferred != null) {
          deferred.reject(response);
        }
      });
  },

  close(templateId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.TEMPLATE_CLOSE,
      templateId,
    });
  },

  setTagSchema(id: string, tagSchema: TagSchema): void {
    TemplateActions.update(id, {$merge: {tagSchema}}, {bumpVersion: false, performTableEvaluation: false});
  },

  setTags(id: string, tags: Array<SchemaTag>): void {
    TemplateActions.update(id, {$merge: {tags}}, {bumpVersion: false, performTableEvaluation: false});
  },
};

export default TemplateActions;
